import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';

/** One active filter, shown as a removable chip. */
export interface FilterChip {
  key: string;
  label: string;
}

/**
 * The list pages' filter card: a search slot on the left, the page's
 * controls beside it, and the active filters as removable chips with
 * "Clear all". Pages keep their filters in the URL; this is layout only.
 *
 *   <admin-filter-bar [chips]="activeChips" (remove)="clear($event)" (clearAll)="clearAll()">
 *     <ion-searchbar search …></ion-searchbar>
 *     <ion-select …></ion-select>
 *   </admin-filter-bar>
 */
@Component({
  selector: 'admin-filter-bar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Unscoped so the compact pill styles reach the projected Ionic controls.
  encapsulation: ViewEncapsulation.None,
  template: `
    <section class="admin-filter-bar" aria-label="Filters">
      <div class="admin-filter-bar__row">
        <div class="admin-filter-bar__search"><ng-content select="[search]"></ng-content></div>
        <div class="admin-filter-bar__controls"><ng-content></ng-content></div>
      </div>
      @if (chips.length) {
        <div class="admin-filter-bar__chips">
          @for (chip of chips; track chip.key) {
            <button
              type="button"
              class="admin-filter-bar__chip"
              [attr.aria-label]="'Remove filter: ' + chip.label"
              (click)="remove.emit(chip.key)"
            >
              {{ chip.label }}
              <span class="material-symbols-outlined" aria-hidden="true">close</span>
            </button>
          }
          <button type="button" class="admin-filter-bar__clear" (click)="clearAll.emit()">Clear all</button>
        </div>
      }
    </section>
  `,
  styles: [
    `
      .admin-filter-bar {
        margin-bottom: 20px;
        padding: 14px 16px;
        border: 1px solid var(--cnt-line);
        border-radius: 1rem;
        background: #fff;
        box-shadow: var(--cnt-card-shadow);
      }
      .admin-filter-bar__row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 10px 12px;
      }
      .admin-filter-bar__search {
        flex: 1 1 260px;
        min-width: 0;
      }
      .admin-filter-bar__search:empty {
        display: none;
      }
      .admin-filter-bar__controls {
        display: flex;
        flex: 1 1 auto;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px;
      }
      @media (max-width: 767px) {
        .admin-filter-bar__controls > * {
          flex: 1 1 100%;
        }
      }
      /* Searchbars (incl. user pickers) as the same cream pills as the selects. */
      .admin-filter-bar ion-searchbar.md,
      .admin-filter-bar ion-searchbar.ios {
        --background: rgba(247, 245, 236, 0.6);
        --border-radius: 9999px;
        --box-shadow: 0 0 0 1px var(--cnt-line-strong);
        padding: 0;
        min-height: 40px;
      }
      /* Compact pill selects and inputs. */
      .admin-filter-bar ion-item {
        --background: rgba(247, 245, 236, 0.6);
        --border-radius: 9999px;
        --padding-start: 14px;
        --inner-padding-end: 10px;
        --min-height: 40px;
        border: 1px solid var(--cnt-line-strong);
        border-radius: 9999px;
        font-size: 0.875rem;
        /* Room for the stacked label and the arrow even when nothing is picked. */
        min-width: 170px;
      }
      .admin-filter-bar ion-select {
        --padding-start: 0;
        min-height: 38px;
        font-size: 0.875rem;
      }
      /* Inputs (e.g. date ranges) match the selects. */
      .admin-filter-bar ion-input {
        --padding-top: 0;
        --padding-bottom: 0;
        min-height: 38px;
        font-size: 0.875rem;
      }
      /* Ionic's stacked inputs are 56px tall; the selects are 38px. */
      .admin-filter-bar ion-input.sc-ion-input-md-h.input-label-placement-stacked {
        min-height: 38px;
      }
      .admin-filter-bar ion-input.input-label-placement-stacked .input-wrapper.sc-ion-input-md {
        min-height: 38px;
      }
      .admin-filter-bar ion-input.input-label-placement-stacked .native-wrapper.sc-ion-input-md {
        min-height: 0;
        height: auto;
      }
      .admin-filter-bar admin-user-picker {
        flex: 1 1 220px;
        min-width: 200px;
      }
      .admin-filter-bar__chips {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px;
        margin-top: 12px;
        padding-top: 12px;
        border-top: 1px solid var(--cnt-line);
      }
      .admin-filter-bar__chip {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 5px 8px 5px 12px;
        border: 1px solid rgba(var(--cnt-trinidad-rgb), 0.3);
        border-radius: 9999px;
        background: rgba(var(--cnt-trinidad-rgb), 0.06);
        color: var(--cnt-trinidad);
        font: inherit;
        font-size: 0.8125rem;
        font-weight: 600;
        cursor: pointer;
      }
      .admin-filter-bar__chip .material-symbols-outlined {
        font-size: 16px;
      }
      .admin-filter-bar__chip:hover {
        background: rgba(var(--cnt-trinidad-rgb), 0.12);
      }
      .admin-filter-bar__clear {
        margin-left: 4px;
        border: 0;
        background: transparent;
        color: var(--cnt-muted);
        font-family: var(--cnt-font-button);
        font-size: 0.7rem;
        font-weight: 700;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        cursor: pointer;
      }
      .admin-filter-bar__clear:hover {
        color: var(--cnt-trinidad);
      }
      .admin-filter-bar__chip:focus-visible,
      .admin-filter-bar__clear:focus-visible {
        outline: 2px solid var(--cnt-green);
        outline-offset: 2px;
      }
    `,
  ],
})
export class AdminFilterBarComponent {
  @Input() chips: FilterChip[] = [];
  /** The chip's key, to reset that one filter. */
  @Output() readonly remove = new EventEmitter<string>();
  @Output() readonly clearAll = new EventEmitter<void>();
}
